import { Button } from '@/components/ui/button';
import Image from 'next/image';
import { Label } from '@/components/ui/label';
import { useState } from 'react';
import { toast } from 'sonner';
import { useUploadFileMutation } from '@/lib/hooks/mutations/use-upload-file-mutation';

import { trainModel } from '@/lib/actions/replicate';
import JSZip from 'jszip';
import { Loader2 } from 'lucide-react';
import { useModelTraining } from './index';
import { useModels } from '@/lib/hooks/queries/useModels';

export default function StartTrainingButton() {
  const {
    modelName,
    modelNameError,
    triggerWord,
    trainingImages,
    setModelName,
    setTriggerWord,
    setTrainingImages,
  } = useModelTraining();
  const [isLoading, setIsLoading] = useState(false);

  // const { mutateAsync: updateUserModel } = useUpdateUserModelMutation();
  const { mutateAsync: uploadFile } = useUploadFileMutation();
  const { refetch: refetchUserModels } = useModels();

  const handleStartTraining = async () => {
    if (trainingImages.length === 0) {
      toast.error('Please select training images first');
      return;
    }

    if (!modelName.trim()) {
      toast.error('Please enter a model name');
      return;
    }

    if (!triggerWord.trim()) {
      toast.error('Please enter a trigger word');
      return;
    }

    setIsLoading(true);

    try {
      // Create zip file
      const zip = new JSZip();
      trainingImages.forEach((file, index) => {
        zip.file(
          `image-${index + 1}${file.name.substring(
            file.name.lastIndexOf('.'),
          )}`,
          file,
        );
      });

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const fileName = `training-${Date.now()}.zip`;
      const zipFile = new File([zipBlob], fileName, {
        type: 'application/zip',
      });

      // Upload zip file
      const result = await uploadFile({
        file: zipFile,
        path: fileName,
      });

      console.log('[StartTrainingButton] result:', result);

      if (result?.publicUrl) {
        const trainingResult = await trainModel({
          modelName: modelName,
          zipUrl: result.publicUrl,
          triggerWord: triggerWord,
        });

        console.log('[StartTrainingButton] trainingResult:', trainingResult);

        // Show success message
        if (trainingResult?.status) {
          toast.success('Training started successfully!');

          // Reset form
          setModelName('');
          setTriggerWord('');
          setTrainingImages([]);

          // Refetch user models data
          refetchUserModels();
        }
      }
    } catch (err) {
      console.error('Training error:', err);
      toast.error('Failed to start training');
    } finally {
      setIsLoading(false);
    }
  };

  // Validation regex for model name
  const validateModelName = (name: string): boolean => {
    if (name.length === 0) return false;
    const regex = /^[a-z0-9][a-z0-9._-]*[a-z0-9]$|^[a-z0-9]$/;
    return regex.test(name);
  };

  const hasMinimumImages = trainingImages.length >= 10;
  const hasValidName = validateModelName(modelName);
  const hasValidTriggerWord = triggerWord.trim().length > 0;
  const isDisabled =
    isLoading ||
    !hasMinimumImages ||
    !hasValidName ||
    !hasValidTriggerWord ||
    Boolean(modelNameError);

  console.log('hasMinimumImages', hasMinimumImages);
  console.log('hasValidName', hasValidName);
  console.log('hasValidTriggerWord', hasValidTriggerWord);
  console.log('modelNameError', modelNameError);
  console.log('isLoading', isLoading);

  const buttonText = isLoading ? 'Starting Training...' : 'Start Training';

  return (
    <div className='px-4 pb-6'>
      {/* Required fields status */}
      <div className='mb-4 p-3 bg-neutral-900/50 rounded-lg border border-neutral-700/50'>
        <Label className='text-sm font-medium text-neutral-300 mb-3 block'>
          Required Fields Status:
        </Label>
        <div className='space-y-2'>
          <div className='flex items-center justify-between'>
            <span className='text-xs text-neutral-400'>Model Name</span>
            <div className='flex items-center gap-2'>
              {hasValidName ? (
                <div className='w-2 h-2 bg-green-500 rounded-full'></div>
              ) : (
                <div className='w-2 h-2 bg-red-500 rounded-full'></div>
              )}
              <span className={`text-xs ${hasValidName ? 'text-green-400' : 'text-red-400'}`}>
                {hasValidName ? 'Valid' : 'Required'}
              </span>
            </div>
          </div>
          
          <div className='flex items-center justify-between'>
            <span className='text-xs text-neutral-400'>Trigger Word</span>
            <div className='flex items-center gap-2'>
              {hasValidTriggerWord ? (
                <div className='w-2 h-2 bg-green-500 rounded-full'></div>
              ) : (
                <div className='w-2 h-2 bg-red-500 rounded-full'></div>
              )}
              <span className={`text-xs ${hasValidTriggerWord ? 'text-green-400' : 'text-red-400'}`}>
                {hasValidTriggerWord ? 'Valid' : 'Required'}
              </span>
            </div>
          </div>
          
          <div className='flex items-center justify-between'>
            <span className='text-xs text-neutral-400'>Training Images</span>
            <div className='flex items-center gap-2'>
              {hasMinimumImages ? (
                <div className='w-2 h-2 bg-green-500 rounded-full'></div>
              ) : (
                <div className='w-2 h-2 bg-red-500 rounded-full'></div>
              )}
              <span className={`text-xs ${hasMinimumImages ? 'text-green-400' : 'text-red-400'}`}>
                {hasMinimumImages ? `${trainingImages.length} images` : `Need at least 10 (${trainingImages.length}/10)`}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className='flex items-center gap-2 mb-2'>
        <Image src='/coin.svg' alt='Coin' width={12} height={12} />
        <Label className='text-stone-50/50 font-inter text-sm'>
          450 credits · Estimated time: 6 minutes
        </Label>
      </div>
      <Button
        size='lg'
        disabled={isDisabled}
        onMouseDown={handleStartTraining}
        className='w-full bg-fuchsia-600 hover:bg-fuchsia-700 text-stone-50/90 font-semibold text-base rounded-lg transition-all flex items-center justify-center gap-2'
      >
        {isLoading ? <Loader2 className='w-5 h-5 animate-spin' /> : null}
        {buttonText}
      </Button>
    </div>
  );
}
